import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete
} from '@mui/material'
import { FC, useState, useEffect } from 'react'
import { CustomModal } from '../../common/CustomModal'
import { AccountingPeriod, CloseAccountingPeriodInput, getMonthName } from './types'
import { Account, PeriodStatus, useFetchAllAccountsLazyQuery } from '../../../../generated'
import InfoIcon from '@mui/icons-material/Info'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../../constants'

interface CloseMonthModalProps {
  isOpen: boolean
  fiscalYear: number
  onClose: () => void
  onConfirm: (input: CloseAccountingPeriodInput) => void
  accountingPeriods: AccountingPeriod[]
  isLoading?: boolean
}

interface AccountOption {
  id: string
  label: string
  sortCode?: string
}

export const CloseMonthModal: FC<CloseMonthModalProps> = ({
  isOpen,
  fiscalYear,
  onClose,
  onConfirm,
  accountingPeriods,
  isLoading = false
}) => {
  const [notes, setNotes] = useState<string>('')
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [accounts, setAccounts] = useState<AccountOption[]>([])

  const getCurrentMonth = (): number => {
    const now = new Date()
    return now.getMonth() + 1 // JavaScript months are 0-indexed
  }

  const getCurrentYear = (): number => {
    return new Date().getFullYear()
  }

  const getAvailableMonths = (): number[] => {
    const closedMonths = accountingPeriods
      .filter(p => p.fiscalYear === fiscalYear && p.status === PeriodStatus.Closed)
      .map(p => p.month)

    const currentYear = getCurrentYear()
    const currentMonth = getCurrentMonth()

    // For past years, allow closing any month (1-12)
    // For current year, allow closing up to current month
    // For future years, allow closing any month (unlikely but handled)
    const maxMonth = fiscalYear < currentYear ? 12 : fiscalYear === currentYear ? currentMonth : 12

    // Return all months from 1 to maxMonth that are not already closed
    const availableMonths: number[] = []
    for (let month = 1; month <= maxMonth; month++) {
      if (!closedMonths.includes(month)) {
        availableMonths.push(month)
      }
    }

    return availableMonths
  }

  const getSuggestedMonth = (): number => {
    const available = getAvailableMonths()
    return available.length > 0 ? available[0] : 1 // Return first available month
  }

  const availableMonths = getAvailableMonths()
  const suggestedMonth = getSuggestedMonth()
  const [selectedMonth, setSelectedMonth] = useState<number>(suggestedMonth)

  // Fetch all active accounts
  const [fetchAccounts, { loading: accountsLoading }] = useFetchAllAccountsLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    variables: undefined,
    onCompleted(data) {
      const fetchedAccounts = (data?.fetchAllAccounts?.accounts ?? []).filter(
        (account): account is Account => account !== null && account.active === true
      )

      setAccounts(
        fetchedAccounts.map(account => ({
          id: account.id || '',
          label: `${account.sortCode ? `(#${account.sortCode})` : ''} ${account.name || ''}`,
          sortCode: account.sortCode || undefined
        }))
      )
    }
  })

  // Fetch accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAccounts({
        variables: {
          accountsInput: {
            active: true,
            paginationOptions: {
              page: 1,
              limit: 1000 // Get all accounts
            }
          }
        }
      })
    }
  }, [isOpen, fetchAccounts])

  // Update selected month when suggested month changes
  useEffect(() => {
    if (suggestedMonth <= 12) {
      setSelectedMonth(suggestedMonth)
    }
  }, [suggestedMonth])

  const handleConfirm = () => {
    onConfirm({
      fiscalYear,
      month: selectedMonth,
      accountId: selectedAccount?.id || undefined, // Include accountId if specific account selected
      notes: notes.trim() || undefined
    })
  }

  const handleOpenConfirm = () => {
    if (isLoading || availableMonths.length === 0) return
    setIsConfirmOpen(true)
  }

  const handleConfirmClose = () => {
    setIsConfirmOpen(false)
  }

  const handleConfirmProceed = () => {
    handleConfirm()
    setIsConfirmOpen(false)
  }

  const handleClose = () => {
    setNotes('')
    setSelectedAccount(null)
    setIsConfirmOpen(false)
    onClose()
  }

  return (
    <CustomModal title="Close Accounting Period" isOpen={isOpen} handleClose={handleClose}>
      <Box sx={{ minWidth: 500 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Close a month to create balance snapshots. You can close any month up to the current month
          {fiscalYear < getCurrentYear() && ' (or all months for past years)'}. Choose to close all accounts at once
          (period close) or reconcile one account at a time (account close).
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="fiscal-year-label">Fiscal Year</InputLabel>
          <Select labelId="fiscal-year-label" label="Fiscal Year" value={fiscalYear} disabled>
            <MenuItem value={fiscalYear}>{fiscalYear}</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="month-label">Month</InputLabel>
          <Select
            labelId="month-label"
            label="Month"
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
            disabled={availableMonths.length === 0}
          >
            {availableMonths.length === 0 ? (
              <MenuItem disabled>
                {fiscalYear === getCurrentYear()
                  ? `All available months are closed`
                  : `All months for ${fiscalYear} are closed`}
              </MenuItem>
            ) : (
              availableMonths.map(month => (
                <MenuItem key={month} value={month}>
                  {getMonthName(month)} {fiscalYear}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* TODO: Uncomment this when needed close single account feature */}
        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Close Scope
        </Typography>

        <Autocomplete
          fullWidth
          options={accounts}
          value={selectedAccount}
          onChange={(_, newValue) => setSelectedAccount(newValue)}
          getOptionLabel={option => option.label}
          loading={accountsLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label="Account (Optional)"
              placeholder="Leave empty to close all accounts"
              helperText={
                selectedAccount
                  ? 'Closing this account only. Period will remain OPEN for other accounts.'
                  : 'Closing all accounts. Period will be marked as CLOSED.'
              }
            />
          )}
          sx={{ mb: 2 }}
        />
        {/* TODO: Uncomment this when needed close single account feature */}

        {selectedAccount && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              p: 2,
              mb: 2,
              bgcolor: 'info.lighter',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'info.light'
            }}
          >
            <InfoIcon fontSize="small" color="info" sx={{ mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Account-Specific Close:</strong> This will create a statement for the selected account only. The
              accounting period will remain OPEN. Use this for progressive reconciliation.
            </Typography>
          </Box>
        )}

        {availableMonths.length === 0 && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {fiscalYear === getCurrentYear()
              ? `All months up to ${getMonthName(
                  getCurrentMonth()
                )} ${fiscalYear} are already closed. To make changes, you must unclose a month first.`
              : `All months for ${fiscalYear} are already closed. To make changes, you must unclose a month first.`}
          </Typography>
        )}

        {availableMonths.length > 0 && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            placeholder="Enter any notes about this month close..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        {/* December Year-End Close Warning */}
        {availableMonths.length > 0 && selectedMonth === 12 && !selectedAccount && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Year-End Close Will Be Triggered
            </Typography>
            <Typography variant="body2" paragraph>
              Closing December will <strong>automatically close Year {fiscalYear}</strong>. The system will:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
              <Typography component="li" variant="body2">
                Calculate net income from all revenue and expense accounts
              </Typography>
              <Typography component="li" variant="body2">
                Create a year-end closing journal entry
              </Typography>
              <Typography component="li" variant="body2">
                Transfer net income to Retained Earnings (Account #3500)
              </Typography>
              <Typography component="li" variant="body2">
                Zero out all income and expense accounts
              </Typography>
              <Typography component="li" variant="body2">
                Set opening balances for {fiscalYear + 1} (Assets, Liabilities, Equity carry forward)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'warning.dark' }}>
              ⚠️ This operation cannot be reversed without unclosing December first.
            </Typography>
          </Alert>
        )}

        <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>

          <Button
            onClick={handleOpenConfirm}
            variant="contained"
            color="primary"
            disabled={isLoading || availableMonths.length === 0}
            endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
          >
            {selectedAccount
              ? `Close Account for ${getMonthName(selectedMonth)}`
              : `Close ${getMonthName(selectedMonth)}`}
          </Button>
        </DialogActions>
      </Box>

      <Dialog open={isConfirmOpen} onClose={handleConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Month Close</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You are about to close{' '}
            <strong>
              {getMonthName(selectedMonth)} {fiscalYear}
            </strong>{' '}
            Accounting Period
            {selectedAccount ? (
              <>
                {' '}
                for <strong>{selectedAccount.label}</strong>
              </>
            ) : (
              <>
                {' '}
                for <strong>all active accounts</strong>
              </>
            )}
            . Journal Entries and Payments can't be posted or modified for this period.
          </Typography>

          {selectedMonth === 12 && !selectedAccount && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
              <Typography variant="body2">
                Closing December will also <strong>automatically close Year {fiscalYear}</strong> (year-end close).
              </Typography>
            </Alert>
          )}

          {!!notes.trim() && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {notes.trim()}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleConfirmClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
          >
            Confirm Close
          </Button>
        </DialogActions>
      </Dialog>
    </CustomModal>
  )
}
